import React from 'react';
import {
  Box,
  Typography,
  useTheme,
  alpha,
  Skeleton,
  Card,
  CardContent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const GOLD = '#c9a96e';

const STAGE_CONFIG = [
  { key: 'draft', colorBase: '#a8a29e', tab: 'drafts' },
  { key: 'in_review', colorBase: '#f59e0b', tab: 'in_review' },
  { key: 'pending_signature', colorBase: '#3b82f6', tab: 'pending_signature' },
  { key: 'signed', colorBase: '#22c55e', tab: 'signed' },
];

const PipelineHealth = ({ lifecycle }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === 'dark';

  const stages = lifecycle.data?.stages || {};

  const labels = [
    t('clmDashboard.pipeline.draft', 'Drafts'),
    t('clmDashboard.pipeline.inReview', 'In Review'),
    t('clmDashboard.pipeline.signing', 'Signing'),
    t('clmDashboard.pipeline.signed', 'Signed'),
  ];

  const handleClick = (tab) => {
    navigate(`/contract?tab=${tab}`);
  };

  if (lifecycle.loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton variant="text" width={180} height={24} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                sx={{ flex: 1, height: 88, borderRadius: '10px' }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  const totalContracts = lifecycle.data?.total_contracts || 0;

  // Calculate bar widths for the progress visualization
  const counts = STAGE_CONFIG.map((s) => (stages[s.key] || {}).total ?? 0);
  const maxCount = Math.max(...counts, 1);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        boxShadow: isDark
          ? '0 2px 8px rgba(0, 0, 0, 0.3)'
          : '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: '0.85rem',
              color: GOLD,
              letterSpacing: '0.02em',
            }}
          >
            {t('clmDashboard.pipelineHealth', 'Pipeline Health')}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
          >
            {totalContracts}{' '}
            {t('clmDashboard.totalContracts', 'total contracts')}
          </Typography>
        </Box>

        {/* Stage cards */}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {STAGE_CONFIG.map((stage, index) => {
            const color = stage.colorBase;
            const stageData = stages[stage.key] || {};
            const count = stageData.total ?? 0;
            const myAction = stageData.my_action_count ?? 0;
            const label = labels[index];
            const barWidth =
              maxCount > 0 ? Math.max((count / maxCount) * 100, 6) : 6;

            return (
              <Box
                key={stage.key}
                onClick={() => handleClick(stage.tab)}
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '10px',
                  p: '14px 12px 12px',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  background: isDark
                    ? alpha(color, 0.05)
                    : alpha(color, 0.04),
                  border: `1px solid ${alpha(color, isDark ? 0.12 : 0.1)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: alpha(color, isDark ? 0.1 : 0.08),
                    borderColor: alpha(color, 0.3),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 16px ${alpha(color, isDark ? 0.15 : 0.1)}`,
                  },
                }}
              >
                {/* Label row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: color,
                      flexShrink: 0,
                      boxShadow: `0 0 6px ${alpha(color, 0.4)}`,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: '0.7rem',
                      letterSpacing: '0.02em',
                      lineHeight: 1,
                    }}
                  >
                    {label}
                  </Typography>
                </Box>

                {/* Count */}
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    fontSize: '1.5rem',
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                    mb: myAction > 0 ? 0.5 : 1,
                  }}
                >
                  {count}
                </Typography>

                {/* "for me" badge */}
                {myAction > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color,
                      fontSize: '0.65rem',
                      mb: 1,
                      lineHeight: 1,
                    }}
                  >
                    {myAction} {t('clmDashboard.forMe', 'for me')}
                  </Typography>
                )}

                {/* Mini progress bar */}
                <Box
                  sx={{
                    width: '100%',
                    height: 3,
                    borderRadius: 2,
                    background: alpha(color, isDark ? 0.1 : 0.08),
                    mt: 'auto',
                  }}
                >
                  <Box
                    sx={{
                      width: `${barWidth}%`,
                      height: '100%',
                      borderRadius: 2,
                      background: `linear-gradient(90deg, ${alpha(color, 0.6)}, ${color})`,
                      transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PipelineHealth;
